import { Button } from "@/components/ui/button";
import { ScreenSpinner } from "@/components/ui/spinner";
import { authClient, tokenManager } from "@/lib/auth/token-manager";
import { scrubLocationSecrets } from "@/lib/auth/url";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";
import { queryClient } from "@/lib/query";
import { request } from "@/lib/request";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { CheckCircle2Icon, LinkIcon, TriangleAlertIcon } from "lucide-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const searchSchema = z.object({
  code: z.string().optional().catch(undefined),
  pending: z.string().optional().catch(undefined),
  status: z.enum(["connected", "error"]).optional().catch(undefined),
  provider: z.string().optional().catch(undefined),
  reason: z.string().optional().catch(undefined),
  client: z.string().optional().catch(undefined),
});

const REASONS: Record<string, string> = {
  CONNECT_LINK_EXPIRED:
    "That connect link expired or was already used. Start again from UniDeck.",
  SIGN_IN_REQUIRED: "Sign in to UniDeck first, then connect this account.",
  STATE_INVALID:
    "This sign-in did not start in this browser, so it was stopped. Please try again.",
  PROVIDER_ERROR:
    "The provider did not complete the sign-in. Please try again.",
  PROVIDER_NOT_FOR_SIGN_IN:
    "This service can be connected to an account, but not used to sign in.",
  PROVIDER_EMAIL_MISSING: "The provider did not share an email address.",
  PROVIDER_EMAIL_UNVERIFIED:
    "Verify your email address with the provider, then try again.",
  ACCOUNT_UNAVAILABLE: "This UniDeck account is no longer available.",
  UPDATE_REQUIRED:
    "Update the UniDeck extension to connect accounts, then try again.",
  CONNECT_OWNER_UNVERIFIED:
    "Verify your UniDeck email address first, or finish this in the browser where you are signed in to UniDeck.",
  CONNECT_ACCOUNT_MISMATCH:
    "This link connects the account to a different UniDeck account than the one signed in here. Start again from the account you want to use.",
};

type PendingConnect = {
  provider: string;
  providerAccount: string | null;
  accountEmail: string;
  sameAccount: boolean | null;
};

const reasonOf = (error: unknown) => {
  const code = isAxiosError(error)
    ? (error.response?.data as { code?: string } | undefined)?.code
    : undefined;
  return code && REASONS[code]
    ? REASONS[code]
    : "Something went wrong, please try again.";
};

const PROVIDER_NAMES: Record<string, string> = {
  github: "GitHub",
  gitlab: "GitLab",
  bitrix24: "Bitrix24",
};

const providerName = (provider?: string) =>
  provider
    ? (PROVIDER_NAMES[provider] ??
      provider.charAt(0).toUpperCase() + provider.slice(1))
    : "Account";

const home = {
  to: "/",
  search: { change_password_token: undefined, reset_password_token: undefined },
} as const;

export const Route = createFileRoute("/connect")({
  validateSearch: searchSchema,
  component: memo(RouteComponent),
});

function RouteComponent() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const started = useRef(false);
  const pendingToken = useRef(search.pending);
  const [state, setState] = useState<
    | { kind: "working" }
    | { kind: "confirm"; pending: PendingConnect }
    | { kind: "connected" }
    | { kind: "error"; message: string }
  >({ kind: "working" });

  const confirmPending = useCallback(
    async (provider: string) => {
      setState({ kind: "working" });
      try {
        await request.post("/auth/connect/confirm", {
          pending: pendingToken.current,
        });
        captureEvent("onboarding_connect_succeeded", {
          service: provider,
          flow: "callback",
        });
        await queryClient.invalidateQueries({ queryKey: ["user"] });
        if (search.client === "extension" || !tokenManager.isSignedIn()) {
          setState({ kind: "connected" });
          return;
        }
        toast.success(`${providerName(provider)} connected`);
        navigate(home);
      } catch (err) {
        captureEvent("onboarding_connect_failed", {
          service: provider,
          flow: "callback",
          reason: sanitizeErrorReason(err),
        });
        setState({ kind: "error", message: reasonOf(err) });
      }
    },
    [navigate, search.client],
  );

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    // Sign-in codes and pending connects are single use; keep them out of
    // history and analytics.
    scrubLocationSecrets(["code", "pending"]);

    const run = async () => {
      if (pendingToken.current) {
        await tokenManager.ready;
        try {
          const response = await request.post<{ data: PendingConnect }>(
            "/auth/connect/pending",
            { pending: pendingToken.current },
          );
          const pending = response.data.data;
          if (pending.sameAccount === true) {
            await confirmPending(pending.provider);
          } else if (pending.sameAccount === false) {
            setState({
              kind: "error",
              message: REASONS.CONNECT_ACCOUNT_MISMATCH,
            });
          } else {
            setState({ kind: "confirm", pending });
          }
        } catch (err) {
          setState({ kind: "error", message: reasonOf(err) });
        }
        return;
      }

      if (search.status === "error") {
        captureEvent("onboarding_connect_failed", {
          service: search.provider ?? "unknown",
          flow: "callback",
          reason: search.reason ?? "unknown",
        });
        setState({
          kind: "error",
          message:
            REASONS[search.reason ?? ""] ??
            "Something went wrong, please try again.",
        });
        return;
      }

      if (search.status === "connected") {
        captureEvent("onboarding_connect_succeeded", {
          service: search.provider ?? "unknown",
          flow: "callback",
        });
        await queryClient.invalidateQueries({ queryKey: ["user"] });
        if (search.client === "extension") {
          setState({ kind: "connected" });
          return;
        }
        toast.success(`${search.provider ?? "Account"} connected`);
        navigate(home);
        return;
      }

      if (!search.code) {
        navigate(home);
        return;
      }

      captureEvent("onboarding_connect_started", {
        service: "account_link",
        flow: "callback",
      });
      try {
        await tokenManager.ready;
        const response = await authClient.post("/auth/session/exchange", {
          code: search.code,
        });
        await tokenManager.applySession(response.data.data);
        localStorage.setItem("unideck.auth.dismissed", "true");
        captureEvent("onboarding_connect_succeeded", {
          service: "account_link",
          flow: "callback",
        });
        navigate(home);
      } catch (err) {
        captureEvent("onboarding_connect_failed", {
          service: "account_link",
          flow: "callback",
          reason: sanitizeErrorReason(err),
        });
        setState({
          kind: "error",
          message: "This sign-in link expired. Please sign in again.",
        });
      }
    };

    void run();
  }, [confirmPending, navigate, search]);

  return (
    <div className="bg-background flex min-h-screen w-screen items-center justify-center px-4">
      {state.kind === "working" ? (
        <div className="flex flex-col gap-4">
          <ScreenSpinner />
          <span className="text-muted-foreground animate-pulse">
            Fetching account information
          </span>
        </div>
      ) : state.kind === "confirm" ? (
        <div className="bg-card flex w-full max-w-sm flex-col items-center gap-4 rounded-xl border p-6 text-center">
          <LinkIcon className="size-10 text-white" />
          <span className="text-lg font-semibold text-white">
            Connect {providerName(state.pending.provider)}?
          </span>
          <span className="text-muted-foreground text-sm">
            {state.pending.providerAccount ? (
              <>
                <span className="text-foreground font-medium">
                  {state.pending.providerAccount}
                </span>{" "}
                will be connected to the UniDeck account
              </>
            ) : (
              "This account will be connected to the UniDeck account"
            )}{" "}
            <span className="text-foreground font-medium break-all">
              {state.pending.accountEmail}
            </span>
            . Only continue if that is your account.
          </span>
          <div className="flex w-full flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="flex-1" asChild>
              <Link {...home}>Cancel</Link>
            </Button>
            <Button
              className="flex-1"
              onClick={() => void confirmPending(state.pending.provider)}
            >
              Connect
            </Button>
          </div>
        </div>
      ) : state.kind === "connected" ? (
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <CheckCircle2Icon className="size-14 text-emerald-400" />
          <span className="text-lg font-semibold text-white">
            {search.provider ? `${search.provider} connected` : "Connected"}
          </span>
          <span className="text-muted-foreground text-sm">
            You can close this tab and go back to UniDeck.
          </span>
          <Button variant="default" asChild>
            <Link {...home}>Open dashboard</Link>
          </Button>
        </div>
      ) : (
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <TriangleAlertIcon className="size-14 text-white" />
          <span className="text-white">{state.message}</span>
          <Button variant="default" asChild>
            <Link {...home}>Go back</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
